import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import PasswordPagePreview, {
  PasswordPreviewScreen,
} from './PasswordPagePreview';
import type { ProductGroup } from '@app/core/models/client';

// Wrapper id mounted per screen; matches the `Card` id the grid-fix CSS scopes
// to, preserving the previous per-screen container behavior.
const WRAPPER_ID: Record<PasswordPreviewScreen, string> = {
  login: 'login-page-preview',
  forgot: 'forgot-password-page-preview',
  change: 'change-password-page-preview',
};

/**
 * Single Angular host for the Log In / Forgot / Change password React
 * previews, selected via `screen`. Replaces the three near-identical
 * per-screen react containers.
 */
@Component({
  selector: 'rn-password-page-preview',
  templateUrl: './password-page-preview.component.html',
  styleUrls: ['./password-page-preview.component.scss'],
  standalone: true,
})
export class PasswordPagePreviewComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  reactWrapperId: string;

  @Input() screen: PasswordPreviewScreen;
  @Input() logoUrl: ProductGroup['branding']['icon_s3_url'];
  @Input() color: ProductGroup['branding']['color'];
  @Input() productGroupName: ProductGroup['name'];
  @Input() showProductGroup: boolean;
  @Input() productGroupDescription: ProductGroup['description'];
  @Input() fdic: ProductGroup['fdic'];
  @Input() showFdic: boolean;
  @Input() language?: string;
  @Input() showLogo?: boolean;
  @Input() showTitleBar?: boolean;
  /** Renders the error state of the selected screen. */
  @Input() isError?: boolean;

  root: Root = null;

  ngOnInit(): void {
    // Unique per instance: the normal and error previews of a screen can be
    // mounted together in the PDF preview.
    this.reactWrapperId = this.isError
      ? `${WRAPPER_ID[this.screen]}-error`
      : WRAPPER_ID[this.screen];
  }

  ngOnDestroy(): void {
    this.root?.unmount();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.render();
  }

  protected render(): void {
    if (this.root) {
      this.root.render(
        React.createElement(PasswordPagePreview, {
          screen: this.screen,
          logoUrl: this.logoUrl,
          color: this.color,
          showProductGroup: this.showProductGroup,
          productGroupName: this.productGroupName,
          productGroupDescription: this.productGroupDescription,
          fdicSignage: this.fdic,
          showFdic: this.showFdic,
          language: this.language,
          showLogo: this.showLogo,
          showTitleBar: this.showTitleBar,
          isError: this.isError,
        }),
      );
    }
  }

  ngAfterViewInit(): void {
    const node = document.getElementById(this.reactWrapperId);
    this.root = createRoot(node);
    this.render();
  }
}
